import { useEffect, useRef, useState } from 'react';
import { useStopRunMutation } from '@/services/api';

export function StopRunButton({
  runId,
  disabled
}: {
  runId: string;
  disabled?: boolean;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const [stopRun, { isLoading }] = useStopRunMutation();
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const confirmRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    if (!open) return;
    confirmRef.current?.focus();
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  const close = (): void => {
    setOpen(false);
    setError(undefined);
    triggerRef.current?.focus();
  };

  const messageFor = (err: unknown): string => {
    const e = err as {
      status?: number | string;
      data?: { errors?: Array<{ message?: string }> };
      error?: string;
    };
    const fromEnvelope = e?.data?.errors?.[0]?.message;
    if (typeof fromEnvelope === 'string' && fromEnvelope !== '') return fromEnvelope;
    if (e?.status === 403) return 'You do not have permission to stop this run.';
    if (e?.status === 404) return 'This run could no longer be found.';
    if (typeof e?.error === 'string' && e.error !== '') return e.error;
    return 'The run could not be stopped. It is still running.';
  };

  const confirm = async (): Promise<void> => {
    try {
      await stopRun({ pipeline_run_id: runId }).unwrap();
      close();
    } catch (err) {
      setError(messageFor(err));
    }
  };

  return (
    <>
      <button
        type="button"
        ref={triggerRef}
        data-testid="stop-run"
        onClick={() => setOpen(true)}
        disabled={disabled || isLoading}
        title={disabled ? 'This run has already finished' : 'Stop this run'}
        className="btn danger sm shrink-0"
      >
        {isLoading ? 'Stopping…' : 'Stop run'}
      </button>

      {open ? (
        <div
          className="overlay open"
          role="dialog"
          aria-modal="true"
          aria-label="Stop this run?"
          data-testid="stop-run-dialog"
          onClick={close}
        >
          <div className="dialog" onClick={(e) => e.stopPropagation()}>
            <div className="dialog-head">
              <h2 className="dialog-title">Stop this run?</h2>
              <p className="dialog-sub">
                The agents working on it will be stopped and the run will be recorded as failed.
                This cannot be undone.
              </p>
            </div>
            {error !== undefined ? (
              <p
                role="alert"
                data-testid="stop-error"
                className="mx-6 mb-3 text-[12.5px] text-err"
              >
                {error}
              </p>
            ) : null}
            <div className="dialog-foot flex justify-end gap-2">
              <button
                type="button"
                data-testid="stop-cancel"
                onClick={close}
                className="btn ghost"
              >
                Keep running
              </button>
              <button
                type="button"
                ref={confirmRef}
                data-testid="stop-confirm"
                onClick={() => void confirm()}
                disabled={isLoading}
                className="btn danger"
              >
                Stop and fail run
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}
